/** @jsx jsx */
import React, { useState, useEffect } from "react";
import { jsx, css } from "@emotion/core";
import { Formik, Field } from "formik";
import { Redirect } from "react-router";
import _get from "lodash/get";
import _map from "lodash/map";

import { get, post } from "../api";
import { colors } from "../../theme";
import { resourceUrls } from "../../config";

import PrimaryCTAButton from "../../components/PrimaryCTA";
import Form from "../../components/Form";
import ValidationError from "../../components/ValidationError";
import RadioButton from "../../components/RadioButton";
import RadioGroup from "../../components/RadioGroup";
import CheckboxGroup, { FormikCheckbox } from "../../components/CheckboxGroup";
import PageContainer from "../components/PageContainer";
import { handleGet, handlePost } from "../api/handler";

const initialValues = {
    codingLanguages: [],
    hasCodingLanguages: "",
};

const formLanguageValidator = values => {
    let errors = {};
    if (values.hasCodingLanguages === "yes") {
        if (values.codingLanguages.length < 1) {
            errors.codingLanguages =
                "Please select at least one coding language.";
        }
    }
    return errors;
};

const hasCodingLanguagesValidator = value => {
    let error;
    if (!value) {
        error = "Please select yes or no.";
    }
    return error;
};

const submitCodingLanguages = (
    setIsLoggedIn,
    setError,
    setFormSubmissionComplete
) => async (values, actions) => {
    const tutorSubjects = _get(values, "codingLanguages").map(
        codingLanguage => ({
            subjectId: codingLanguage,
            levelId: 4,
        })
    );

    const body = {
        applicationStep: "CODING_LANGUAGE",
        tutorSubjects,
    };

    await handlePost({
        postApiCall: () =>
            post({ url: resourceUrls.codingLanguages.submit, body: body }),
        setIsLoggedIn: setIsLoggedIn,
        setError: setError,
        setIsSubmissionComplete: setFormSubmissionComplete,
    });

    actions.setSubmitting(false);
};

const getCodingSubjects = async (
    setIsLoading,
    setIsLoggedIn,
    setError,
    setCodingLanguages
) => {
    await handleGet({
        getApiCall: () =>
            get({ url: resourceUrls.codingLanguages.getCodingSubjects }),
        setIsLoading,
        setIsLoggedIn,
        setError,
        dataTransform: data =>
            _map(data, subjectLevel => _get(subjectLevel, "subject")),
        setData: setCodingLanguages,
    });
};

const CodingLanguages = () => {
    const [codingLanguages, setCodingLanguages] = useState([]);

    const [isLoading, setIsLoading] = useState(true);
    const [isLoggedIn, setIsLoggedIn] = useState(true);
    const [error, setError] = useState(null);
    const [formSubmissionComplete, setFormSubmissionComplete] = useState(false);

    useEffect(() => {
        getCodingSubjects(
            setIsLoading,
            setIsLoggedIn,
            setError,
            setCodingLanguages
        );
    }, []);

    if (formSubmissionComplete) {
        return (
            <Redirect
                push
                to={{
                    pathname: "/secure/apply",
                }}
            />
        );
    }

    return (
        <PageContainer
            isLoading={isLoading}
            error={error}
            isLoggedIn={isLoggedIn}
        >
            <h2>
                Are there any coding languages you'd feel confident tutoring?
            </h2>
            <p>Please select an option:</p>

            <Formik
                initialValues={initialValues}
                validate={formLanguageValidator}
                validateOnBlur={false}
                validateOnChange={false}
                onSubmit={submitCodingLanguages(
                    setIsLoggedIn,
                    setError,
                    setFormSubmissionComplete
                )}
                render={({ isSubmitting, ...props }) => (
                    <Form onSubmit={props.handleSubmit}>
                        <RadioGroup>
                            <Field
                                validate={hasCodingLanguagesValidator}
                                component={RadioButton}
                                name="hasCodingLanguages"
                                id="yes"
                                data-testid="yes"
                                label="Yes"
                            />
                        </RadioGroup>
                        {props.values.hasCodingLanguages === "yes" && (
                            <CheckboxGroup
                                data-testid="coding-languages"
                                additionalStyles={css`
                                    border-left: 8px solid ${colors.neutral2};
                                    & label {
                                        margin-left: 12px;
                                    }
                                `}
                            >
                                {codingLanguages.length > 0 ? (
                                    codingLanguages.map(codingLanguage => (
                                        <FormikCheckbox
                                            name="codingLanguages"
                                            value={codingLanguage.id}
                                            label={codingLanguage.name}
                                            data-testid={`coding-languages-checkbox-${codingLanguage.name}`}
                                        />
                                    ))
                                ) : (
                                    <ValidationError data-testid="error-retrieving-coding-languages">
                                        Error retrieving coding languages.
                                    </ValidationError>
                                )}
                            </CheckboxGroup>
                        )}
                        <RadioGroup>
                            <div>
                                <Field
                                    validate={hasCodingLanguagesValidator}
                                    component={RadioButton}
                                    name="hasCodingLanguages"
                                    id="no"
                                    data-testid="no"
                                    label="No"
                                />
                            </div>
                        </RadioGroup>
                        {props.errors.hasCodingLanguages && (
                            <ValidationError data-testid="has-coding-languages-validation-message">
                                {props.errors.hasCodingLanguages}
                            </ValidationError>
                        )}
                        {props.errors.codingLanguages && (
                            <ValidationError data-testid="has-a-coding-language-validation-message">
                                {props.errors.codingLanguages}
                            </ValidationError>
                        )}
                        <PrimaryCTAButton
                            type="submit"
                            disabled={isSubmitting}
                            data-testid="submit"
                        >
                            Next →
                        </PrimaryCTAButton>
                    </Form>
                )}
            />
        </PageContainer>
    );
};

export default CodingLanguages;
